import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Play, BarChart3, Shield, ChevronLeft,
  ChevronRight, X, AlertTriangle, CheckCircle2, ChevronDown,
  ChevronUp, Loader2, ArrowLeft, Info, Layers, Eye,
} from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import { udqApi } from '../../services/api';
import UDQLanding from './UDQLanding';
import type { UDQReport, UDQDimensionScore } from '../../types';

const STEPS = ['Upload', 'Run Quality Test', 'Report Results', 'AI Ready DQ Score'];

const ACCEPTED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/tiff',
];

const DOC_TYPES = [
  { value: 'auto', label: 'Auto Detect' },
  { value: 'fnol', label: 'FNOL (First Notice of Loss)' },
  { value: 'medical_records', label: 'Medical Records' },
  { value: 'police_report', label: 'Police Report' },
  { value: 'generic', label: 'Generic Document' },
];

const INDUSTRIES = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'financial', label: 'Financial' },
  { value: 'default', label: 'Default' },
];

/* ─── Helpers ─── */

function gradeColor(grade: string) {
  switch (grade) {
    case 'A': return 'text-emerald-400';
    case 'B': return 'text-blue-400';
    case 'C': return 'text-yellow-400';
    case 'D': return 'text-orange-400';
    case 'F': return 'text-red-400';
    default: return 'text-cargo-muted';
  }
}

function scoreBarColor(score: number) {
  if (score >= 90) return 'bg-emerald-500';
  if (score >= 75) return 'bg-blue-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

function severityBadge(severity: string) {
  const map: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-red-500/20 text-red-400 border-red-500/30',
    medium: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    info: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return map[severity] || map.info;
}

/* ─── 10-Dimension Model Data ─── */
const DIMENSIONS_TABLE = [
  { dimension: 'Processability', question: 'Can we reliably read this document?', protects: 'Poor scans, OCR failures, unreadable submissions', impact: 'Prevents downstream automation failure' },
  { dimension: 'Completeness', question: 'Are all required data elements present?', protects: 'Missing fields, placeholders, incomplete submissions', impact: 'Reduces rework and processing delays' },
  { dimension: 'Accuracy', question: 'Are extracted values valid and trustworthy?', protects: 'Incorrect SSNs, invalid dates, malformed identifiers', impact: 'Improves data integrity' },
  { dimension: 'Consistency', question: 'Does the document contradict itself?', protects: 'Logical conflicts, date errors, cross-document mismatches', impact: 'Prevents claim/payment errors' },
  { dimension: 'Currency', question: 'Is the document still valid?', protects: 'Expired forms, outdated policies, stale reports', impact: 'Avoids regulatory and operational risk' },
  { dimension: 'Duplication', question: 'Has this been submitted before?', protects: 'Duplicate claims, resubmitted forms', impact: 'Reduces fraud and redundancy' },
  { dimension: 'Conformity', question: 'Does it match expected structural standards?', protects: 'Abnormal format, missing metadata, structural anomalies', impact: 'Ensures workflow compatibility' },
  { dimension: 'Referential Integrity', question: 'Do referenced attachments actually exist?', protects: 'Missing supporting documents', impact: 'Prevents incomplete case processing' },
  { dimension: 'Semantic Coherence', question: 'Does the content logically make sense?', protects: 'Garbled text, irrelevant submissions', impact: 'Improves review efficiency' },
  { dimension: 'Provenance', question: 'Can we trust where it came from?', protects: 'Tampered files, stripped metadata', impact: 'Strengthens audit defensibility' },
];

const SAMPLE_RISKS = [
  { label: 'Logical date conflict', dimension: 'Consistency', severity: 'high' },
  { label: 'Missing referenced police report', dimension: 'Referential Integrity', severity: 'medium' },
  { label: 'Weak source metadata', dimension: 'Provenance', severity: 'medium' },
  { label: 'Suboptimal scan resolution', dimension: 'Processability', severity: 'low' },
];

/* ─── Framework Overview Component ─── */
function FrameworkOverview({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <div className="bg-gradient-to-br from-purple-500/8 via-violet-500/5 to-indigo-500/8 border border-purple-400/30 rounded-xl overflow-hidden mb-6">
      {/* Header — always visible */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-purple-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
            <Layers className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-cargo-text">Unstructured Data Quality Intelligence Framework</h2>
            <p className="text-xs text-cargo-muted">10-dimension quality model for unstructured data</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-cargo-muted">
          <Eye className="w-4 h-4" />
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </div>
      </button>

      {/* Expandable content */}
      {!collapsed && (
        <div className="px-6 pb-6 space-y-6">
          {/* Description */}
          <div className="space-y-2">
            <p className="text-sm text-cargo-text leading-relaxed">
              Unstructured Data Quality Intelligence Engine evaluates every document across <strong>ten critical quality dimensions</strong> to
              ensure it is readable, complete, accurate, consistent, current, non-duplicative, structurally valid, logically coherent, and trustworthy.
            </p>
            <p className="text-sm text-cargo-muted leading-relaxed">
              Each document receives a dimension-level diagnostic, risk classification, composite quality score (0-100), and actionable remediation insights.
            </p>
          </div>

          {/* 10-Dimension Table */}
          <div>
            <h3 className="text-sm font-bold text-cargo-text mb-3">10-Dimension Quality Model</h3>
            <div className="overflow-x-auto rounded-lg border border-cargo-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-cargo-panel text-cargo-muted text-xs uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-medium">Dimension</th>
                    <th className="text-left px-4 py-2.5 font-medium">Business Question</th>
                    <th className="text-left px-4 py-2.5 font-medium">What It Protects Against</th>
                    <th className="text-left px-4 py-2.5 font-medium">Business Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cargo-border bg-cargo-surface">
                  {DIMENSIONS_TABLE.map((d) => (
                    <tr key={d.dimension} className="hover:bg-cargo-hover/30 transition-colors">
                      <td className="px-4 py-2.5 font-medium text-cargo-text whitespace-nowrap">{d.dimension}</td>
                      <td className="px-4 py-2.5 text-cargo-muted">{d.question}</td>
                      <td className="px-4 py-2.5 text-cargo-muted">{d.protects}</td>
                      <td className="px-4 py-2.5 text-cargo-muted">{d.impact}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sample Executive Scorecard */}
          <div>
            <h3 className="text-sm font-bold text-cargo-text mb-3">Sample Executive Scorecard</h3>
            <div className="bg-cargo-surface border border-cargo-border rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-cargo-muted">Document Type Template</p>
                  <p className="text-sm font-semibold text-cargo-text">First Notice of Loss (FNOL)</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-cargo-muted">Overall Quality Score</p>
                  <p className="text-2xl font-bold text-blue-500">82 <span className="text-sm text-cargo-muted font-normal">/ 100</span></p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-cargo-muted mb-2">Key Risk Indicators</p>
                <div className="space-y-1.5">
                  {SAMPLE_RISKS.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${severityBadge(r.severity)}`}>
                        {r.severity}
                      </span>
                      <span className="text-cargo-text">{r.label}</span>
                      <span className="text-cargo-muted">({r.dimension})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */

export default function UDQWizard() {
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('auto');
  const [industry, setIndustry] = useState('insurance');
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [frameworkCollapsed, setFrameworkCollapsed] = useState(true);
  const [report, setReport] = useState<UDQReport | null>(null);
  const runTriggered = useRef(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  /* File handling */
  const handleFile = useCallback((f: File) => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      setError('Unsupported file type. Please upload PDF, PNG, JPG, or TIFF.');
      return;
    }
    setFile(f);
    setError('');
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  /* Run assessment */
  const runAssessment = useCallback(async () => {
    if (!file || running) return;
    setRunning(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', documentType);
      formData.append('industry', industry);
      const resp = await udqApi.assessDocument(formData);
      setReport(resp.data.payload);
      setStep(2);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Assessment failed. Please try again.');
    } finally {
      setRunning(false);
    }
  }, [file, documentType, industry, running]);

  /* Auto-trigger run when entering step 1 */
  const goToRun = useCallback(() => {
    setStep(1);
    runTriggered.current = false;
  }, []);

  /* Navigation */
  const next = () => {
    if (step === 0) {
      goToRun();
    } else if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const back = () => {
    if (step === 0) {
      setShowWizard(false);
      setFile(null);
      setReport(null);
      setError('');
      setStep(0);
    } else {
      setStep(step - 1);
    }
  };

  if (!showWizard) {
    return (
      <div className="min-h-screen bg-cargo-bg p-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-cargo-text">Unstructured Data Quality Assessment (UDQ)</h1>
            <p className="text-cargo-muted mt-1">
              View past assessments or start a new document quality analysis
            </p>
          </div>
          <UDQLanding onNewAssessment={() => setShowWizard(true)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cargo-bg p-6">
      {/* Header */}
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={back} className="text-cargo-muted hover:text-cargo-text">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-cargo-text">Unstructured Data Quality Assessment (UDQ)</h1>
        </div>

        {/* Framework overview */}
        <FrameworkOverview collapsed={frameworkCollapsed} onToggle={() => setFrameworkCollapsed(!frameworkCollapsed)} />

        {/* Step indicator */}
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-4 mb-6">
          <StepIndicator steps={STEPS} current={step} />
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-cargo-error flex-shrink-0" />
            <p className="text-sm text-cargo-error flex-1">{error}</p>
            <button onClick={() => setError('')} className="text-cargo-error hover:text-cargo-error/80">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step content */}
        {step === 0 && (
          <UploadStep
            file={file}
            documentType={documentType}
            industry={industry}
            dragOver={dragOver}
            fileInputRef={fileInputRef}
            onFile={handleFile}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDocTypeChange={setDocumentType}
            onIndustryChange={setIndustry}
            onRemoveFile={() => { setFile(null); setReport(null); }}
          />
        )}

        {step === 1 && (
          <RunStep
            file={file}
            documentType={documentType}
            industry={industry}
            running={running}
            onRun={runAssessment}
          />
        )}

        {step === 2 && report && <ReportStep report={report} />}

        {step === 3 && report && <ScoreStep report={report} />}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-6">
          <button
            onClick={back}
            disabled={running}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-cargo-border text-cargo-muted hover:text-cargo-text hover:border-cargo-accent/50 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 0 ? 'Home' : 'Back'}
          </button>

          {step === 0 && (
            <button
              onClick={next}
              disabled={!file}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Run Assessment
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {step === 2 && (
            <button
              onClick={next}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors"
            >
              View DQ Score
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Step 0: Upload
   ═══════════════════════════════════════════════ */

interface UploadStepProps {
  file: File | null;
  documentType: string;
  industry: string;
  dragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFile: (f: File) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDocTypeChange: (v: string) => void;
  onIndustryChange: (v: string) => void;
  onRemoveFile: () => void;
}

function UploadStep({
  file, documentType, industry, dragOver, fileInputRef,
  onFile, onDrop, onDragOver, onDragLeave, onDocTypeChange, onIndustryChange, onRemoveFile,
}: UploadStepProps) {
  return (
    <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Connect Source</h2>

      {/* Dropzone */}
      {!file ? (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
            dragOver
              ? 'border-cargo-accent bg-cargo-accent/5'
              : 'border-cargo-border hover:border-cargo-accent/50'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto text-cargo-muted mb-4" />
          <p className="text-cargo-text font-medium mb-1">
            Drag & drop your document here
          </p>
          <p className="text-sm text-cargo-muted">
            or click to browse. Supports PDF, PNG, JPG, TIFF
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.tiff,.tif"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
        </div>
      ) : (
        <div className="flex items-center gap-4 bg-cargo-panel rounded-lg p-4">
          <FileText className="w-10 h-10 text-cargo-accent flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-cargo-text truncate">{file.name}</p>
            <p className="text-xs text-cargo-muted">
              {(file.size / 1024).toFixed(1)} KB &middot; {file.type || 'Unknown type'}
            </p>
          </div>
          <button onClick={onRemoveFile} className="text-cargo-muted hover:text-cargo-error">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div>
          <label className="block text-sm font-medium text-cargo-muted mb-1.5">Document Type Template</label>
          <select
            value={documentType}
            onChange={(e) => onDocTypeChange(e.target.value)}
            className="w-full bg-cargo-panel border border-cargo-border rounded-lg px-3 py-2 text-sm text-cargo-text focus:border-cargo-accent focus:outline-none"
          >
            {DOC_TYPES.map((dt) => (
              <option key={dt.value} value={dt.value}>{dt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-cargo-muted mb-1.5">Domain</label>
          <select
            value={industry}
            onChange={(e) => onIndustryChange(e.target.value)}
            className="w-full bg-cargo-panel border border-cargo-border rounded-lg px-3 py-2 text-sm text-cargo-text focus:border-cargo-accent focus:outline-none"
          >
            {INDUSTRIES.map((ind) => (
              <option key={ind.value} value={ind.value}>{ind.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Step 1: Run Quality Test
   ═══════════════════════════════════════════════ */

interface RunStepProps {
  file: File | null;
  documentType: string;
  industry: string;
  running: boolean;
  onRun: () => void;
}

function RunStep({ file, documentType, industry, running, onRun }: RunStepProps) {
  return (
    <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
      <h2 className="text-lg font-semibold text-cargo-text mb-4">Run Quality Test</h2>

      {/* Context cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <InfoCard icon={<FileText className="w-5 h-5 text-cargo-accent" />} label="Document" value={file?.name || '—'} />
        <InfoCard icon={<BarChart3 className="w-5 h-5 text-purple-400" />} label="Type" value={DOC_TYPES.find((d) => d.value === documentType)?.label || documentType} />
        <InfoCard icon={<Shield className="w-5 h-5 text-emerald-400" />} label="Domain" value={INDUSTRIES.find((i) => i.value === industry)?.label || industry} />
      </div>

      {/* Run area */}
      <div className="text-center py-8">
        {running ? (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-cargo-accent animate-spin mb-4" />
            <p className="text-cargo-text font-medium">Running quality assessment...</p>
            <p className="text-sm text-cargo-muted mt-1">
              Processing document with OCR, entity extraction, and 10-dimension scoring
            </p>
          </>
        ) : (
          <>
            <Play className="w-16 h-16 mx-auto text-cargo-accent mb-4" />
            <p className="text-cargo-text font-medium mb-4">Ready to assess document quality</p>
            <button
              onClick={onRun}
              className="px-8 py-3 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors"
            >
              Start Assessment
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div className="min-w-0">
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text truncate">{value}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Step 2: Report Results
   ═══════════════════════════════════════════════ */

function ReportStep({ report }: { report: UDQReport }) {
  const dims = Object.values(report.dimensions).sort((a, b) => a.dimension.localeCompare(b.dimension));
  const [showText, setShowText] = useState(false);

  return (
    <div className="space-y-4">
      {/* Processing summary */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
        <h2 className="text-lg font-semibold text-cargo-text mb-4">Processing Summary</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Text Length" value={`${report.processing.text_length.toLocaleString()} chars`} />
          <StatCard label="Entities Found" value={String(report.processing.entities_found)} />
          <StatCard label="Pages" value={String(report.processing.page_count)} />
          <StatCard label="OCR Confidence" value={`${report.processing.ocr_confidence_avg}%`} />
        </div>
        {/* Image Metrics */}
        {report.processing.image_metrics && (
          <div className="mt-4">
            <p className="text-xs text-cargo-muted mb-2">Image Metrics</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="DPI" value={String(report.processing.image_metrics.dpi)} />
              <StatCard label="Contrast" value={report.processing.image_metrics.contrast.toFixed(1)} />
              <StatCard label="Blur" value={report.processing.image_metrics.blur.toFixed(1)} />
              <StatCard label="Skew" value={`${report.processing.image_metrics.skew.toFixed(1)}\u00b0`} />
            </div>
          </div>
        )}
      </div>

      {/* Extracted Entities */}
      {report.processing.extracted_entities?.length > 0 && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
          <h2 className="text-lg font-semibold text-cargo-text mb-4">Extracted Entities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {report.processing.extracted_entities.map((entity, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-cargo-panel rounded-lg px-3 py-2">
                <span className="text-[10px] font-medium text-cargo-accent bg-cargo-accent/10 rounded px-1.5 py-0.5 uppercase w-32 text-center truncate">
                  {entity.type.replace(/_/g, ' ')}
                </span>
                <span className="text-sm text-cargo-text truncate flex-1">{entity.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extracted Text */}
      {report.processing.extracted_text && (
        <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
          <button
            onClick={() => setShowText(!showText)}
            className="flex items-center gap-2 text-lg font-semibold text-cargo-text"
          >
            Extracted Text (OCR)
            {showText ? <ChevronUp className="w-4 h-4 text-cargo-muted" /> : <ChevronDown className="w-4 h-4 text-cargo-muted" />}
          </button>
          {showText && (
            <pre className="mt-3 bg-cargo-panel rounded-lg p-4 text-xs text-cargo-text whitespace-pre-wrap max-h-64 overflow-y-auto">
              {report.processing.extracted_text}
            </pre>
          )}
        </div>
      )}

      {/* Issues summary */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
        <h2 className="text-lg font-semibold text-cargo-text mb-4">Issues Summary</h2>
        <div className="flex gap-3">
          <IssueBadge severity="critical" count={report.issues_summary.critical} />
          <IssueBadge severity="high" count={report.issues_summary.high} />
          <IssueBadge severity="medium" count={report.issues_summary.medium} />
          <IssueBadge severity="low" count={report.issues_summary.low} />
        </div>
      </div>

      {/* Dimension cards */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
        <h2 className="text-lg font-semibold text-cargo-text mb-4">Dimension Scores</h2>
        <div className="space-y-3">
          {dims.map((dim) => (
            <DimensionCard key={dim.dimension} dim={dim} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-cargo-panel rounded-lg p-3 text-center">
      <p className="text-xs text-cargo-muted mb-1">{label}</p>
      <p className="text-lg font-semibold text-cargo-text">{value}</p>
    </div>
  );
}

function IssueBadge({ severity, count }: { severity: string; count: number }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${severityBadge(severity)}`}>
      {severity}: {count}
    </span>
  );
}

const DIMENSION_INFO: Record<string, { description: string; explains: Record<string, string> }> = {
  processability: {
    description: 'Can the document be read and processed by machines? Measures image quality, OCR readability, and scan clarity.',
    explains: {
      dpi: 'Resolution in dots per inch. 300+ is ideal, below 150 is poor.',
      contrast: 'Text-to-background contrast. Higher means more legible.',
      skew: 'Document tilt angle in degrees. 0 is perfectly straight.',
      blur: 'Image sharpness (Laplacian variance). Lower is sharper, very high means blurry.',
      ocr: 'Percentage of words Textract could read with high confidence.',
    },
  },
  completeness: {
    description: 'Are all expected fields present in the document? Checks required, expected, and optional fields against the document profile.',
    explains: {
      fields: 'Weighted score of found vs. expected fields (required=1.0, expected=0.7, optional=0.3).',
      placeholders: 'Penalizes placeholder values like TBD, N/A, TODO.',
      attachments: 'Penalizes references to missing external attachments.',
      pages: 'Checks if all expected pages are present (e.g., "Page 1 of 3").',
    },
  },
  accuracy: {
    description: 'Are the extracted values valid and correct? Validates entity formats (SSN, VIN, email, phone) using check-digit algorithms and patterns.',
    explains: {
      syntactic: 'Format validation — do values match expected patterns (e.g., SSN: XXX-XX-XXXX)?',
      plausibility: 'Are values within reasonable ranges (dates, amounts)?',
      ocr: 'OCR confidence for entity values — low confidence may mean misread characters.',
    },
  },
  consistency: {
    description: 'Does the document agree with itself? Checks for conflicting values, date ordering, and cross-document consistency.',
    explains: {
      intra: 'No duplicate/conflicting values for the same entity type within the document.',
      cross_field: 'Logical field relationships hold (e.g., date_of_loss <= date_reported).',
      cross_doc: 'Consistency with other documents in the same claim cluster.',
    },
  },
  currency: {
    description: 'Is the document up to date? Checks document age, expiration dates, and references to outdated standards.',
    explains: {
      age: 'How old is the document? Compared against thresholds per document type.',
      version: 'Does the document contain "superseded" or "obsolete" keywords?',
      regulatory: 'References to outdated standards (e.g., ICD-9 instead of ICD-10).',
      expiry: 'Are any dates (policy expiry, license) past their expiration?',
    },
  },
  duplication: {
    description: 'Is this a duplicate of another document? Uses exact hash matching and SimHash near-duplicate detection.',
    explains: {
      exact: 'Binary-identical file check (SHA-256 hash).',
      simhash: 'Text similarity fingerprint — catches near-duplicates with minor edits.',
      entity_overlap: 'Jaccard similarity of entity values across documents.',
    },
  },
  conformity: {
    description: 'Does the document match its expected structure? Checks page count, word count, metadata, and structural format.',
    explains: {
      pages: 'Is the estimated page count within the expected range for this document type?',
      words: 'Does the document meet minimum word count requirements?',
      metadata: 'Are required metadata fields (filename, MIME type, date) present?',
      structure: 'Does the document follow the expected structural layout?',
    },
  },
  referential_integrity: {
    description: 'Do document references resolve? Checks if referenced documents, policy numbers, and external reports are available.',
    explains: {
      doc_refs: 'References like "see attached report" — is the referenced document in the cluster?',
      entity_refs: 'Are reference numbers (policy, claim, case) present and trackable?',
      external: 'Can external references (databases, registries) be resolved?',
    },
  },
  semantic_coherence: {
    description: 'Does the content make logical sense? Evaluates narrative flow, factual consistency, language quality, and topic relevance.',
    explains: {
      narrative: 'Is the text structured in coherent sentences with logical flow?',
      factual: 'Are stated facts internally consistent and plausible?',
      linguistic: 'Text quality — checks for excessive non-ASCII characters or garbled OCR.',
      topical: 'Does the content match expected keywords for this document type?',
    },
  },
  provenance: {
    description: 'Can we trust the document source? Checks for source identification, authenticity markers, and modification detection.',
    explains: {
      source: 'Are source metadata fields present (channel, system, author)?',
      authenticity: 'Digital signature, creation/modification date consistency.',
      modification: 'Signs of metadata stripping or document tampering.',
    },
  },
};

function DimensionCard({ dim }: { dim: UDQDimensionScore }) {
  const [open, setOpen] = useState(false);
  const name = dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const info = DIMENSION_INFO[dim.dimension];

  return (
    <div className="bg-cargo-panel rounded-lg border border-cargo-border overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-cargo-hover/30 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-medium text-cargo-text">{name}</span>
            <span className={`text-xs font-bold ${gradeColor(dim.grade)}`}>{dim.grade}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-cargo-border rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${scoreBarColor(dim.score)}`} style={{ width: `${dim.score}%` }} />
            </div>
            <span className="text-xs text-cargo-muted w-10 text-right">{dim.score.toFixed(0)}</span>
          </div>
        </div>
        {dim.issues.length > 0 && (
          <span className="text-xs text-cargo-muted">{dim.issues.length} issue{dim.issues.length !== 1 ? 's' : ''}</span>
        )}
        {open ? <ChevronUp className="w-4 h-4 text-cargo-muted" /> : <ChevronDown className="w-4 h-4 text-cargo-muted" />}
      </button>

      {/* Expanded details */}
      {open && (
        <div className="px-4 pb-4 border-t border-cargo-border pt-3 space-y-3">
          {/* Description */}
          {info && (
            <p className="text-xs text-cargo-muted italic">{info.description}</p>
          )}
          {/* Sub-scores with explanations */}
          {Object.keys(dim.sub_scores).length > 0 && (
            <div>
              <p className="text-xs text-cargo-muted mb-2">Sub-Scores</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(dim.sub_scores).map(([key, val]) => {
                  const numVal = typeof val === 'number' ? val : 0;
                  const explanation = info?.explains?.[key];
                  return (
                    <div key={key} className="bg-cargo-surface rounded px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[11px] font-medium text-cargo-text capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className={`text-sm font-bold ${numVal >= 80 ? 'text-emerald-400' : numVal >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {typeof val === 'number' ? val.toFixed(0) : val}
                        </p>
                      </div>
                      <div className="h-1 bg-cargo-border rounded-full overflow-hidden mb-1">
                        <div className={`h-full rounded-full ${scoreBarColor(numVal)}`} style={{ width: `${numVal}%` }} />
                      </div>
                      {explanation && (
                        <p className="text-[10px] text-cargo-muted leading-tight">{explanation}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Issues */}
          {dim.issues.length > 0 && (
            <div>
              <p className="text-xs text-cargo-muted mb-2">Issues Found</p>
              <div className="space-y-1.5">
                {dim.issues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs bg-cargo-surface rounded px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border flex-shrink-0 ${severityBadge(issue.severity)}`}>
                      {issue.severity}
                    </span>
                    <span className="text-cargo-text">{issue.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* No issues message */}
          {dim.issues.length === 0 && (
            <div className="flex items-center gap-2 text-xs text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              No issues detected in this dimension.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Step 3: AI Ready DQ Score
   ═══════════════════════════════════════════════ */

function ScoreStep({ report }: { report: UDQReport }) {
  const { composite } = report;
  const circumference = 2 * Math.PI * 70;
  const offset = circumference - (composite.dqs / 100) * circumference;

  return (
    <div className="space-y-4">
      {/* Score gauge */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-8 text-center">
        <h2 className="text-lg font-semibold text-cargo-text mb-6">AI Ready Data Quality Score</h2>

        <div className="relative w-48 h-48 mx-auto mb-6">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="70" fill="none" stroke="currentColor" className="text-cargo-border" strokeWidth="10" />
            <circle
              cx="80" cy="80" r="70" fill="none"
              strokeWidth="10" strokeLinecap="round"
              className={composite.dqs >= 75 ? 'text-emerald-500' : composite.dqs >= 60 ? 'text-yellow-500' : 'text-red-500'}
              stroke="currentColor"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-4xl font-bold text-cargo-text">{composite.dqs.toFixed(1)}</span>
            <span className="text-sm text-cargo-muted">/ 100</span>
          </div>
        </div>

        {/* Grade + label */}
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl font-bold border-2 ${
            composite.grade === 'A' ? 'border-emerald-500 text-emerald-400 bg-emerald-500/10' :
            composite.grade === 'B' ? 'border-blue-500 text-blue-400 bg-blue-500/10' :
            composite.grade === 'C' ? 'border-yellow-500 text-yellow-400 bg-yellow-500/10' :
            composite.grade === 'D' ? 'border-orange-500 text-orange-400 bg-orange-500/10' :
            'border-red-500 text-red-400 bg-red-500/10'
          }`}>
            {composite.grade}
          </div>
          <div className="text-left">
            <p className="text-lg font-semibold text-cargo-text">{composite.label}</p>
            <p className="text-sm text-cargo-muted">{composite.action.replace(/_/g, ' ')}</p>
          </div>
        </div>

        {/* AI Safety flag — clickable when not safe */}
        <AiSafetyBadge report={report} />
      </div>

      {/* Dimension breakdown mini-bars */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
        <h3 className="text-sm font-semibold text-cargo-text mb-4">Dimension Breakdown</h3>
        <div className="space-y-1">
          {Object.values(report.dimensions)
            .sort((a, b) => b.score - a.score)
            .map((dim) => (
              <ScoreBreakdownRow key={dim.dimension} dim={dim} />
            ))}
        </div>
      </div>

      {/* Report metadata */}
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <p className="text-cargo-muted">Report ID</p>
            <p className="text-cargo-text font-medium mt-0.5">{report.report_id}</p>
          </div>
          <div>
            <p className="text-cargo-muted">Document</p>
            <p className="text-cargo-text font-medium mt-0.5">{report.source_name}</p>
          </div>
          <div>
            <p className="text-cargo-muted">Module</p>
            <p className="text-cargo-text font-medium mt-0.5 uppercase">{report.module}</p>
          </div>
          <div>
            <p className="text-cargo-muted">Timestamp</p>
            <p className="text-cargo-text font-medium mt-0.5">{new Date(report.timestamp).toLocaleString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreBreakdownRow({ dim }: { dim: UDQDimensionScore }) {
  const [showDesc, setShowDesc] = useState(false);
  const name = dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const info = DIMENSION_INFO[dim.dimension];

  return (
    <div>
      <div className="flex items-center gap-3 py-1.5">
        <div className="w-36 shrink-0 flex items-center gap-1.5">
          <span className="text-xs text-cargo-muted truncate">{name}</span>
          {info && (
            <button
              onClick={() => setShowDesc((v) => !v)}
              className={`shrink-0 p-0.5 rounded transition-colors ${
                showDesc ? 'text-cargo-accent' : 'text-cargo-muted/40 hover:text-cargo-muted'
              }`}
              title={`What is ${name}?`}
            >
              <Info className="w-3 h-3" />
            </button>
          )}
        </div>
        <div className="flex-1 h-2 bg-cargo-border rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${scoreBarColor(dim.score)}`} style={{ width: `${dim.score}%` }} />
        </div>
        <span className="text-xs font-medium text-cargo-text w-8 text-right">{dim.score.toFixed(0)}</span>
        <span className={`text-xs font-bold w-4 ${gradeColor(dim.grade)}`}>{dim.grade}</span>
      </div>
      {showDesc && info && (
        <div className="ml-[9.5rem] mr-16 mb-2 px-3 py-2 bg-cargo-accent/5 border border-cargo-accent/15 rounded-lg text-[11px] text-cargo-muted leading-relaxed">
          {info.description}
        </div>
      )}
    </div>
  );
}

function AiSafetyBadge({ report }: { report: UDQReport }) {
  const [open, setOpen] = useState(false);
  const { composite } = report;

  if (composite.ai_safe) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        <CheckCircle2 className="w-4 h-4" /> AI Safe
      </div>
    );
  }

  // Build reasons
  const reasons: string[] = [];
  if (composite.dqs < composite.threshold) {
    reasons.push(`Overall score ${composite.dqs.toFixed(1)} is below the ${composite.threshold} threshold`);
  }
  const lowDims = Object.values(report.dimensions).filter((d) => d.enabled && d.score < 40);
  if (lowDims.length > 0) {
    const names = lowDims.map((d) =>
      `${d.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (${d.score.toFixed(0)})`
    ).join(', ');
    reasons.push(`Dimension below critical minimum (40): ${names}`);
  }
  const criticalDims: string[] = [];
  for (const dim of Object.values(report.dimensions)) {
    const critCount = dim.issues.filter((i) => i.severity === 'critical').length;
    if (critCount > 0) {
      criticalDims.push(
        `${dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} (${critCount})`
      );
    }
  }
  if (criticalDims.length > 0) {
    reasons.push(`Critical issues found in: ${criticalDims.join(', ')}`);
  }
  if (reasons.length === 0) {
    reasons.push('One or more AI safety conditions were not met');
  }

  return (
    <div className="flex flex-col items-center">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors cursor-pointer"
      >
        <AlertTriangle className="w-4 h-4" />
        Not AI Safe
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-3 max-w-md text-left bg-red-500/5 border border-red-500/15 rounded-lg px-4 py-3">
          <p className="text-xs font-semibold text-red-400 mb-2">Why not AI Safe?</p>
          <ul className="space-y-1.5">
            {reasons.map((r, i) => (
              <li key={i} className="text-xs text-cargo-muted flex items-start gap-2">
                <span className="text-red-400 mt-0.5 shrink-0">&#x2022;</span>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
